import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Edit3, BarChart3, Calendar, TrendingUp, Trash2, HelpCircle } from 'lucide-react';
import { Habit, HabitMetricsDisplay } from '../types';
import { useHabits } from '../contexts/HabitsContext';
import HabitHistoryView from './HabitHistoryView';
import CustomValueChart from './CustomValueChart';
import styles from './HabitDetailsModal.module.css';

interface HabitDetailsModalProps {
  habit: Habit;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  onOpenTour?: () => void;
}

const HabitDetailsModal: React.FC<HabitDetailsModalProps> = ({ habit, isOpen, onClose, onDelete, onOpenTour }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedHabit, setEditedHabit] = useState<Habit>(habit);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [customValueHistory, setCustomValueHistory] = useState<any[]>([]);
  const [habitMetrics, setHabitMetrics] = useState<HabitMetricsDisplay | null>(null);
  const [habitStats, setHabitStats] = useState({
    currentStreak: 0,
    successRate: 0,
    completionsThisWeek: Array(7).fill(false)
  });
  // Função para gerar as datas dos últimos 7 dias com dados completos
  const getLast7DaysData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const label = `${day}/${month}`;
      const dateStr = date.toISOString().split('T')[0]; // yyyy-MM-dd para comparação
      
      data.push({ label, dateStr, date: new Date(date) });
    }
    
    // 🐛 CORREÇÃO: Verificar integridade dos dados gerados
    const uniqueDates = new Set(data.map(d => d.dateStr));
    const uniqueLabels = new Set(data.map(d => d.label));
    
    if (uniqueDates.size !== 7 || uniqueLabels.size !== 7) {
      console.error('🚨 ERRO CRÍTICO: getLast7DaysData gerou dados inválidos!', {
        totalItems: data.length,
        uniqueDates: uniqueDates.size,
        uniqueLabels: uniqueLabels.size,
        data: data.map(d => ({ label: d.label, dateStr: d.dateStr }))
      });
      
      // Tentar regenerar com método alternativo mais robusto
      const fallbackData = [];
      const baseDate = new Date();
      baseDate.setHours(12, 0, 0, 0); // Meio-dia para evitar problemas de fuso
      
      for (let i = 6; i >= 0; i--) {
        const targetDate = new Date(baseDate.getTime() - (i * 24 * 60 * 60 * 1000));
        const day = targetDate.getDate().toString().padStart(2, '0');
        const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
        const label = `${day}/${month}`;
        const dateStr = targetDate.toISOString().split('T')[0];
        
        fallbackData.push({ label, dateStr, date: new Date(targetDate) });
      }
      
      console.log('🔧 Dados regenerados com método alternativo:', fallbackData.map(d => ({ label: d.label, dateStr: d.dateStr })));
      return fallbackData;
    }
    
    return data;
  };

  const last7DaysData = getLast7DaysData();
  const last7DaysLabels = last7DaysData.map(d => d.label);
  
  const { updateHabit, deleteHabit, getHabitHistory, isHabitCompletedToday, getCurrentStreak, getHabitMetrics } = useHabits();
  
  // Inicialização do modal (com debounce para evitar múltiplas execuções)
  useEffect(() => {
    if (!isOpen || !habit) return;

    console.log('🚀 HabitDetailsModal - Abrindo para hábito:', habit.id);

    const timeoutId = setTimeout(() => {
      // Garantir que todos os campos tenham valores padrão
      setEditedHabit({
        ...habit,
        hasCustomValue: habit.hasCustomValue || false,
        customValueLabel: habit.customValueLabel || '',
        customValueUnit: habit.customValueUnit || '',
        customValueType: habit.customValueType || 'number'
      });
      setIsEditing(false);
      calculateHabitStats();
      loadCustomValueHistory();
      loadHabitMetrics();
    }, 50); // Debounce de 50ms

    return () => clearTimeout(timeoutId);
  }, [isOpen, habit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalcular estatísticas quando o estado de conclusão mudar (com debounce otimizado)
  useEffect(() => {
    if (!isOpen || !habit) return;
    
    const isCompleted = isHabitCompletedToday(habit.id);
    console.log('🔄 Estado de conclusão:', { habitId: habit.id, isCompleted });
    
    // Debounce mais agressivo para evitar múltiplas execuções
    const timeoutId = setTimeout(() => {
      console.log('📈 Atualizando estatísticas após mudança de estado');
      calculateHabitStats();
      
      // Carregar dados apenas se for hábito com valor personalizado
      if (habit.hasCustomValue) {
        if (habit.customValueType === 'number') {
          loadHabitMetrics();
        }
        loadCustomValueHistory();
      }
    }, 150); // Debounce de 150ms
    
    return () => clearTimeout(timeoutId);
  }, [isHabitCompletedToday(habit?.id), isOpen, habit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug para acompanhar estado do modal de confirmação
  useEffect(() => {
    console.log('🔍 showDeleteConfirm mudou para:', showDeleteConfirm);
    if (showDeleteConfirm) {
      console.log('🔍 Modal de confirmação deveria estar visível agora');
    }
  }, [showDeleteConfirm]);

  // Carregar métricas para hábitos numéricos
  const loadHabitMetrics = async () => {
    if (!habit?.id || !habit.hasCustomValue || habit.customValueType !== 'number') {
      setHabitMetrics(null);
      return;
    }

    try {
      console.log('📊 Carregando métricas para:', habit.id);
      const metrics = await getHabitMetrics(habit.id);
      setHabitMetrics(metrics);
      console.log('✅ Métricas obtidas:', metrics ? 'Carregadas' : 'Vazias');
    } catch (error) {
      console.error('❌ Erro ao carregar métricas:', error instanceof Error ? error.message : String(error));
      setHabitMetrics(null);
    }
  };

  const loadCustomValueHistory = async () => {
    if (!habit?.id || !habit.hasCustomValue) return;

    try {
      // Usar mesmo range do HabitHistoryView para consistência (3 meses)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90); // 3 meses = ~90 dias
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      console.log('📊 Carregando histórico (90 dias):', habit.id);
      
      const history = await getHabitHistory(habit.id, startDateStr, endDateStr);
      console.log('✅ Histórico obtido:', history.length, 'registros');
      setCustomValueHistory(history);
    } catch (error) {
      console.error('❌ Erro ao carregar histórico:', error instanceof Error ? error.message : String(error));
      setCustomValueHistory([]);
    }
  };

  const calculateHabitStats = async () => {
    if (!habit?.id) return;

    try {
      console.log('📊 Calculando estatísticas:', habit.id);
      
      // Calcular período dos últimos 30 dias para estatísticas gerais
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 29); // 30 dias no total
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Buscar histórico do hábito
      const history = await getHabitHistory(habit.id, startDateStr, endDateStr);
      
      // CORRIGIR O ESTADO DE HOJE no histórico para cálculos gerais
      const today = new Date().toISOString().split('T')[0];
      const isCompletedToday = isHabitCompletedToday(habit.id);
      
      // Criar uma cópia do histórico com o estado atual de hoje
      const correctedHistory = [...history];
      const todayIndex = correctedHistory.findIndex(day => day.date === today);
      
      if (todayIndex !== -1) {
        // Atualizar hoje no histórico
        correctedHistory[todayIndex] = {
          ...correctedHistory[todayIndex],
          completed: isCompletedToday
        };
      } else {
        // Adicionar hoje se não estiver no histórico
        correctedHistory.push({
          date: today,
          completed: isCompletedToday
        });
      }
      
      // Ordenar por data
      correctedHistory.sort((a, b) => a.date.localeCompare(b.date));
      
      // Calcular taxa de sucesso com o histórico corrigido (últimos 30 dias)
      const completedDays = correctedHistory.filter(day => day.completed).length;
      const totalDays = correctedHistory.length;
      const successRate = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;
      
      // Calcular sequência atual usando a função otimizada do contexto
      const currentStreak = getCurrentStreak(habit.id);
      
        // Calcular últimos 7 dias corretamente (usando estado atual para hoje)
      // 🐛 CORREÇÃO: Verificar duplicatas e normalizar dados antes do mapeamento
      const todayStr = new Date().toISOString().split('T')[0];
      const todayCompletedState = isHabitCompletedToday(habit.id);
        // Verificar se há duplicatas nas datas geradas
      const dateStrs = last7DaysData.map(d => d.dateStr);
      const uniqueDateStrs = Array.from(new Set(dateStrs));
      if (uniqueDateStrs.length !== 7) {
        console.warn('⚠️ PROBLEMA: Duplicatas detectadas nos últimos 7 dias!', { dateStrs, uniqueDateStrs });
      }
      
      const completionsThisWeek = last7DaysData.map((dayData, index) => {
        // Verificar se é hoje
        const isToday = dayData.dateStr === todayStr;
        
        if (isToday) {
          // Para hoje, usar o estado atual do contexto
          return todayCompletedState;
        } else {
          // Para outros dias, usar o histórico original
          const historyDay = history.find(h => h.date === dayData.dateStr);
          return historyDay ? historyDay.completed : false;
        }
      });
      
      console.log('✅ Estatísticas calculadas:', { 
        streak: currentStreak, 
        successRate: `${successRate}%`, 
        completed7Days: completionsThisWeek.filter(Boolean).length 
      });
      
      setHabitStats({
        currentStreak,
        successRate,
        completionsThisWeek
      });
      
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas:', error);
      // Manter stats zerados em caso de erro
      setHabitStats({
        currentStreak: 0,
        successRate: 0,
        completionsThisWeek: Array(7).fill(false)
      });
    }
  };

  const handleSave = async () => {
    if (!editedHabit.name.trim()) return;
    
    setIsSaving(true);
    try {
      const updates: any = {
        name: editedHabit.name.trim(),
        description: editedHabit.description?.trim() || '',
        duration: editedHabit.duration,
        difficulty: editedHabit.difficulty,
        hasCustomValue: Boolean(editedHabit.hasCustomValue)
      };

      // Só incluir campos customizados se hasCustomValue for true
      if (editedHabit.hasCustomValue) {
        if (editedHabit.customValueLabel && editedHabit.customValueLabel.trim()) {
          updates.customValueLabel = editedHabit.customValueLabel.trim();
        }
        if (editedHabit.customValueUnit && editedHabit.customValueUnit.trim()) {
          updates.customValueUnit = editedHabit.customValueUnit.trim();
        }
        updates.customValueType = editedHabit.customValueType || 'number';
      }

      await updateHabit(habit.id, updates);
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao atualizar hábito:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedHabit({
      ...habit,
      hasCustomValue: habit.hasCustomValue || false,
      customValueLabel: habit.customValueLabel || '',
      customValueUnit: habit.customValueUnit || '',
      customValueType: habit.customValueType || 'number'
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    console.log('🗑️ handleDelete chamado para hábito:', habit.id, habit.name);
    setIsDeleting(true);
    try {
      console.log('🔥 Chamando deleteHabit...');
      await deleteHabit(habit.id);
      console.log('✅ Hábito deletado com sucesso');
      setShowDeleteConfirm(false);
      onDelete?.();
      onClose();
    } catch (error) {
      console.error('❌ Erro ao deletar hábito:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Principal */}
      <AnimatePresence>
        <motion.div
          key="habit-details-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={styles.backdrop}
          onClick={onClose}
        >
          <motion.div
            key="habit-details-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={styles.modalContent} //fundo do modal
            onClick={(e) => e.stopPropagation()}
          >            {/* Header */}
            <div className={styles.header} data-tour="modal-header">
              <h2 className={styles.title}>Detalhes do Hábito</h2>              <div className={styles.headerButtons}>
                <button
                  onClick={() => onOpenTour && onOpenTour()}
                  className={`${styles.headerButton} ${styles.helpButton}`}
                  title="Guia: Como usar detalhes do hábito"
                  data-tour="help-button"
                >
                  <HelpCircle size={20} />
                </button>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`${styles.headerButton} ${styles.editButton}`}
                  title="Editar hábito"
                  data-tour="edit-button"
                >
                  <Edit3 size={20} />
                </button>
                <button
                  onClick={() => {
                    console.log('🗑️ Botão de excluir clicado');
                    setShowDeleteConfirm(true);
                  }}
                  className={`${styles.headerButton} ${styles.deleteButton}`}
                  title="Excluir hábito"
                  data-tour="delete-button"
                >
                  <Trash2 size={20} />
                </button>
                <button
                  onClick={onClose}
                  className={`${styles.headerButton} ${styles.closeButton}`}
                  title="Fechar"
                >
                  <X size={20} />
                </button>
              </div>
            </div>            {/* Content */}
            <div className={styles.content}>
              <div className={styles.contentSpacing}>
                {isEditing ? (
                  /* Formulário de Edição */
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Nome do Hábito
                      </label>
                      <input
                        type="text"
                        value={editedHabit.name}
                        onChange={(e) => setEditedHabit({...editedHabit, name: e.target.value})}
                        className={styles.input}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Descrição
                      </label>
                      <textarea
                        value={editedHabit.description || ''}
                        onChange={(e) => setEditedHabit({...editedHabit, description: e.target.value})}
                        className={styles.textarea}
                        rows={3}
                      />
                    </div>

                    <div className={styles.gridCols2}>                      <div className={styles.formGroup}>
                        <label className={styles.label}>
                          Duração (min)
                        </label>
                        <div className={styles.durationInputContainer}>
                          <button
                            type="button"
                            className={styles.durationButton}
                            onClick={() => setEditedHabit({
                              ...editedHabit, 
                              duration: Math.max(1, (editedHabit.duration || 1) - 1)
                            })}
                            disabled={(editedHabit.duration || 1) <= 1}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min="1"
                            max="120"
                            value={editedHabit.duration || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                setEditedHabit({...editedHabit, duration: undefined as any});
                              } else {
                                const num = parseInt(value);
                                if (!isNaN(num) && num >= 1 && num <= 120) {
                                  setEditedHabit({...editedHabit, duration: num});
                                }
                              }
                            }}
                            onBlur={() => {
                              if (!editedHabit.duration || editedHabit.duration === 0) {
                                setEditedHabit({...editedHabit, duration: 5});
                              }
                            }}
                            className={styles.durationInput}
                            placeholder="5"
                          />
                          <button
                            type="button"
                            className={styles.durationButton}
                            onClick={() => setEditedHabit({
                              ...editedHabit, 
                              duration: Math.min(120, (editedHabit.duration || 1) + 1)
                            })}
                            disabled={(editedHabit.duration || 1) >= 120}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.label}>
                          Dificuldade
                        </label>
                        <select
                          value={editedHabit.difficulty}
                          onChange={(e) => setEditedHabit({...editedHabit, difficulty: e.target.value as any})}
                          className={styles.select}
                        >
                          <option value="facil">Fácil</option>
                          <option value="medio">Médio</option>
                          <option value="dificil">Difícil</option>
                        </select>
                      </div>
                    </div>

                    {/* Campos Personalizados */}
                    <div className={styles.customFields}>
                      <div className={styles.checkboxGroup}>
                        <input
                          type="checkbox"
                          id="hasCustomValue"
                          checked={editedHabit.hasCustomValue}
                          onChange={(e) => setEditedHabit({...editedHabit, hasCustomValue: e.target.checked})}
                          className={styles.checkbox}
                        />
                        <label htmlFor="hasCustomValue" className={styles.label}>
                          Permitir valores personalizados
                        </label>
                      </div>

                      {editedHabit.hasCustomValue && (
                        <div className={styles.gridCols2}>
                          <div className={styles.formGroup}>
                            <label className={styles.label}>
                              Rótulo do Valor
                            </label>
                            <input
                              type="text"
                              value={editedHabit.customValueLabel || ''}
                              onChange={(e) => setEditedHabit({...editedHabit, customValueLabel: e.target.value})}
                              placeholder="Ex: Tempo, Distância, Peso"
                              className={styles.input}
                            />
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.label}>
                              Unidade
                            </label>
                            <input
                              type="text"
                              value={editedHabit.customValueUnit || ''}
                              onChange={(e) => setEditedHabit({...editedHabit, customValueUnit: e.target.value})}
                              placeholder="Ex: min, km, kg, reps"
                              className={styles.input}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.actionButtons}>                      <button
                        onClick={handleSave}
                        disabled={isSaving || !editedHabit.name.trim() || !editedHabit.duration || editedHabit.duration <= 0}
                        className={styles.primaryButton}
                      >
                        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                      </button>
                      <button
                        onClick={handleCancel}
                        className={styles.secondaryButton}
                      >
                        Cancelar
                      </button>
                    </div>
                  </>                ) : (
                  /* Visualização de Detalhes */
                  <>
                    {/* Info do Hábito */}
                    <div className={styles.habitInfo}>
                      <h3 className={styles.habitName}>{habit.name}</h3>
                      {habit.description && (
                        <p className={styles.habitDescription}>{habit.description}</p>
                      )}
                      <div className={styles.habitMeta}>
                        <span className={styles.metaItem}>
                          <Calendar size={16} />
                          <span>{habit.duration} min</span>
                        </span>
                        <span className={`${styles.difficultyBadge} ${
                          habit.difficulty === 'facil' ? styles.difficultyFacil :
                          habit.difficulty === 'medio' ? styles.difficultyMedio :
                          styles.difficultyDificil
                        }`}>
                          {habit.difficulty.charAt(0).toUpperCase() + habit.difficulty.slice(1)}
                        </span>
                        {habit.hasCustomValue && (
                          <span className={styles.customValueBadge}>
                            <BarChart3 size={16} />
                            <span>{habit.customValueLabel || 'Valor personalizado'}</span>
                          </span>
                        )}
                      </div>
                    </div>                    {/* Estatísticas Reais */}
                    <div className={styles.statsGrid} data-tour="stats-section">
                      <div className={styles.statCard}>
                        <div className={`${styles.statValue} ${styles.statBlue}`}>{habitStats.currentStreak}</div>
                        <div className={styles.statLabel}>Sequência Atual</div>
                      </div>
                      <div className={styles.statCard}>
                        <div className={`${styles.statValue} ${styles.statGreen}`}>{habitStats.successRate}%</div>
                        <div className={styles.statLabel}>Taxa de Sucesso</div>
                      </div>
                    </div>                    {/* Gráfico dos últimos 7 dias - clicável para abrir calendário */}
                    <div 
                      className={`${styles.weeklyChart} ${styles.clickableChart}`} 
                      data-tour="weekly-chart"
                      onClick={() => setShowHistory(true)}
                      title="Clique para abrir o calendário completo"
                    >
                      <h4 className={styles.chartTitle}>
                        <TrendingUp size={20} />
                        <span>Últimos 7 Dias</span>
                        <span className={styles.clickHint}>📅 Clique para ver calendário</span>
                      </h4><div className={styles.chartContainer}>
                        {last7DaysLabels.map((dateLabel, index) => (
                          <div key={`day-${index}`} className={styles.dayColumn}>
                            <div className={`${styles.dayLabel} ${
                              habitStats.completionsThisWeek[index] 
                                ? styles.dayCompleted
                                : styles.dayIncomplete
                            }`}>
                              {dateLabel}
                            </div>
                            <div className={`${styles.dayBar} ${
                              habitStats.completionsThisWeek[index] ? styles.barCompleted : styles.barIncomplete
                            }`}></div>
                          </div>
                        ))}
                      </div>
                    </div>                    {/* Histórico de Valores Personalizados */}
                    {habit.hasCustomValue && habit.customValueType === 'number' && (
                      <CustomValueChart
                        data={customValueHistory}
                        customValueLabel={habit.customValueLabel}
                        customValueUnit={habit.customValueUnit}
                        className={styles.customValueChart}
                      />
                    )}

                    {/* Placeholder para valores de texto */}
                    {habit.hasCustomValue && habit.customValueType === 'text' && (
                      <div className={styles.historySection} data-tour="custom-values">
                        <h4 className={styles.chartTitle}>
                          <BarChart3 size={20} />
                          <span>Histórico de {habit.customValueLabel || 'Valores'}</span>
                        </h4>
                        <div className={styles.historyEmpty}>
                          <p className={styles.historyEmptyTitle}>Valores de texto registrados</p>
                          <p className={styles.historyEmptySubtitle}>
                            Acesse o calendário para ver todos os registros
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Métricas Numéricas - apenas para hábitos com valores personalizados numéricos */}
                    {habit.hasCustomValue && habit.customValueType === 'number' && habitMetrics && (
                      <div className={styles.metricsSection} data-tour="numeric-metrics">
                        <h4 className={styles.metricsTitle}>
                          <BarChart3 size={20} />
                          Métricas Numéricas
                        </h4>
                        
                        <div className={styles.metricsGrid}>
                          {/* Métricas do Ano Atual */}
                          <div className={styles.metricCard}>
                            <div className={styles.metricHeader}>Ano Atual</div>
                            <div className={styles.metricValues}>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Total:</span>
                                <span className={`${styles.metricValue} ${styles.metricBlue}`}>
                                  {habitMetrics.year.sum} {habit.customValueUnit || ''}
                                </span>
                              </div>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Média:</span>
                                <span className={`${styles.metricValue} ${styles.metricGreen}`}>
                                  {habitMetrics.year.average?.toFixed(1) || '0'} {habit.customValueUnit || ''}
                                </span>
                              </div>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Entradas:</span>
                                <span className={`${styles.metricValue} ${styles.metricPurple}`}>
                                  {habitMetrics.year.count}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Métricas do Mês Atual */}
                          <div className={styles.metricCard}>
                            <div className={styles.metricHeader}>Mês Atual</div>
                            <div className={styles.metricValues}>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Total:</span>
                                <span className={`${styles.metricValue} ${styles.metricBlue}`}>
                                  {habitMetrics.month.sum} {habit.customValueUnit || ''}
                                </span>
                              </div>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Média:</span>
                                <span className={`${styles.metricValue} ${styles.metricGreen}`}>
                                  {habitMetrics.month.average?.toFixed(1) || '0'} {habit.customValueUnit || ''}
                                </span>
                              </div>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Entradas:</span>
                                <span className={`${styles.metricValue} ${styles.metricPurple}`}>
                                  {habitMetrics.month.count}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Métricas da Semana Atual */}
                          <div className={styles.metricCard}>
                            <div className={styles.metricHeader}>Semana Atual</div>
                            <div className={styles.metricValues}>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Total:</span>
                                <span className={`${styles.metricValue} ${styles.metricBlue}`}>
                                  {habitMetrics.week.sum} {habit.customValueUnit || ''}
                                </span>
                              </div>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Média:</span>
                                <span className={`${styles.metricValue} ${styles.metricGreen}`}>
                                  {habitMetrics.week.average?.toFixed(1) || '0'} {habit.customValueUnit || ''}
                                </span>
                              </div>
                              <div className={styles.metricItem}>
                                <span className={styles.metricLabel}>Entradas:</span>
                                <span className={`${styles.metricValue} ${styles.metricPurple}`}>
                                  {habitMetrics.week.count}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Histórico Anual - apenas se houver dados */}
                        {habitMetrics.yearlyHistory && habitMetrics.yearlyHistory.length > 0 && (
                          <div className={styles.yearlyHistory}>
                            <h5 className={styles.historyTitle}>Histórico Anual</h5>
                            <div className={styles.historyGrid}>
                              {habitMetrics.yearlyHistory.map((yearData) => (
                                <div key={yearData.year} className={styles.historyCard}>
                                  <div className={styles.historyYear}>{yearData.year}</div>
                                  <div className={styles.historyValue}>
                                    {yearData.sum} {habit.customValueUnit || ''}
                                  </div>
                                  <div className={styles.historyCount}>
                                    {yearData.count} entradas
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>      {/* Modal de Confirmação - Renderizado via Portal no body */}
      {showDeleteConfirm && createPortal(
        <div className={styles.confirmModal}>
          <div className={styles.confirmContent}>
            <div className={styles.confirmCenter}>
              <div className={styles.confirmIcon}>
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              
              <h3 className={styles.confirmTitle}>
                Excluir Hábito
              </h3>
              
              <p className={styles.confirmText}>
                Tem certeza que deseja excluir o hábito "{habit.name}"?
              </p>
              
              <p className={styles.confirmWarning}>
                ⚠️ Esta ação irá remover:
                <br />• O hábito da sua lista
                <br />• Todas as configurações do hábito
                <br />
                <span className={styles.confirmNote}>
                  Nota: O histórico de conclusões será mantido.
                </span>
              </p>
              
              <div className={styles.confirmButtons}>
                <button
                  onClick={() => {
                    console.log('🔍 Cancelar clicado');
                    setShowDeleteConfirm(false);
                  }}
                  className={styles.secondaryButton}
                  disabled={isDeleting}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    console.log('🔍 Excluir Definitivamente clicado');
                    handleDelete();
                  }}
                  disabled={isDeleting}
                  className={styles.dangerButton}
                > 
                  {isDeleting ? 'Excluindo...' : 'Excluir Definitivamente'}
                </button>
              </div>
            </div>
          </div>
        </div>,        document.body
      )}
      
      {/* Histórico Modal */}
      <HabitHistoryView
        habit={habit}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
    </>
  );
};

export default HabitDetailsModal;
